import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ethers } from "ethers";
import {
  FiCheck,
  FiCheckCircle,
  FiArrowRight,
  FiLoader,
  FiAlertCircle,
  FiCopy,
  FiExternalLink,
} from "react-icons/fi";
import ComponentWrapper from "../components/Shared/ComponentWrapper";
import AddHLXButton from "../components/Shared/AddHLXButton";

/* ──────────────────────────── Constants ──────────────────────────── */

// Base network (source)
const BASE_CHAIN_ID = "0x2105"; // 8453
const BASE_PARAMS = {
  chainId: BASE_CHAIN_ID,
  chainName: "Base",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: ["https://mainnet.base.org"],
  blockExplorerUrls: ["https://basescan.org"],
};

// Ethereum mainnet (destination)
// eslint-disable-next-line no-unused-vars
const ETHEREUM_CHAIN_ID = "0x1"; // 1

// HLX token address on Base (source network). The balance read is best-effort and
// never blocks the migration flow.
const BASE_HLX_TOKEN_ADDRESS = "0xd7e6c76cc2b945f90d9af84fada08eb968b8edc5";
const HLX_DECIMALS = 18;
const ERC20_ABI = ["function balanceOf(address owner) view returns (uint256)"];

const PROCESS_STEPS = [
  "Verifying wallet ownership signature",
  "Scanning & reversing AI agent wallet funding",
  "Calculating 1:1 migration amount",
  "Queuing distribution on Ethereum",
];

const STEPPER = ["Connect", "Verify & Sign", "Migrate", "Done"];

// Maps a wizard stage to the active stepper index.
const STAGE_TO_STEP = {
  connect: 0,
  review: 1,
  processing: 2,
  success: 3,
};

/* ──────────────────────────── Helpers ──────────────────────────── */

const truncate = (addr) =>
  addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "";

const formatAmount = (amt) => {
  if (amt === null || amt === undefined) return null;
  const num = Number(amt);
  if (!Number.isFinite(num)) return amt;
  return num.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const randomNonce = () => {
  try {
    const bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch (e) {
    return Math.random().toString(16).slice(2);
  }
};

const friendlyError = (err) => {
  if (!err) return "Something went wrong. Please try again.";
  if (err.code === 4001 || /user rejected|user denied/i.test(err.message || "")) {
    return "Request cancelled in your wallet. Please approve it to continue.";
  }
  return err.message || "Something went wrong. Please try again.";
};

/* ──────────────────────────── Page ──────────────────────────── */

const MigratePage = () => {
  const [stage, setStage] = useState("intro"); // intro | connect | review | processing | success
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceError, setBalanceError] = useState(false);
  const [signature, setSignature] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [processStep, setProcessStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const cardRef = useRef(null);
  const intervalRef = useRef(null);

  // Scroll to top on mount.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll the wizard card into comfortable view on stage change.
  useEffect(() => {
    if (stage !== "intro" && cardRef.current) {
      const top =
        cardRef.current.getBoundingClientRect().top + window.pageYOffset - 110;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [stage]);

  // Drive the processing checklist animation.
  useEffect(() => {
    if (stage !== "processing") return undefined;
    setProcessStep(0);
    intervalRef.current = setInterval(() => {
      setProcessStep((prev) => {
        const next = prev + 1;
        if (next >= PROCESS_STEPS.length) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          // Allow the final check to render before switching screens.
          setTimeout(() => setStage("success"), 700);
          return PROCESS_STEPS.length;
        }
        return next;
      });
    }, 950);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [stage]);

  // Persist the result when we reach success.
  useEffect(() => {
    if (stage === "success" && referenceId && account) {
      try {
        localStorage.setItem(
          "hlx_migration_result",
          JSON.stringify({
            account,
            referenceId,
            balance,
            ts: new Date().toISOString(),
          })
        );
      } catch (e) {
        /* ignore storage failures */
      }
    }
  }, [stage, referenceId, account, balance]);

  /* ── Wallet helpers ── */

  const switchToBase = async () => {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: BASE_CHAIN_ID }],
      });
    } catch (err) {
      if (err?.code === 4902) {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [BASE_PARAMS],
        });
      } else {
        throw err;
      }
    }
  };

  const connectAndLoad = async () => {
    setError(null);
    if (!window.ethereum) {
      setError(
        "MetaMask not detected. Please install MetaMask to migrate your HLX."
      );
      return;
    }
    setBusy(true);
    try {
      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts",
      });
      const addr = accounts && accounts[0];
      if (!addr) {
        setError("No wallet account was found. Please unlock your wallet.");
        setBusy(false);
        return;
      }
      setAccount(addr);

      // Make sure we are reading from Base.
      await switchToBase();

      // Best-effort balance read — never blocks the flow.
      setBalanceError(false);
      setBalance(null);
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const token = new ethers.Contract(
          BASE_HLX_TOKEN_ADDRESS,
          ERC20_ABI,
          provider
        );
        const raw = await token.balanceOf(addr);
        setBalance(ethers.utils.formatUnits(raw, HLX_DECIMALS));
      } catch (balErr) {
        setBalanceError(true);
      }

      setStage("review");
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const signOwnershipClaim = async () => {
    setError(null);
    if (!window.ethereum || !account) {
      setError("Wallet not connected. Please reconnect and try again.");
      setStage("connect");
      return;
    }
    setBusy(true);
    try {
      const nonce = randomNonce();
      const timestamp = new Date().toISOString();
      const amountLine =
        balance !== null && !balanceError
          ? `${formatAmount(balance)} HLX`
          : "to be verified from on-chain records";

      const message = [
        "Helix HLX Migration — Wallet Ownership Claim",
        "",
        "I confirm that I am the rightful owner of the wallet below, which was",
        "KYC-verified during the Helix token sale. I authorize Helix to:",
        "  1. Reverse any AI agent wallet funding associated with this wallet.",
        "  2. Migrate my HLX balance 1:1 from Base to Ethereum.",
        "",
        `Wallet: ${account}`,
        "Source: Base (chainId 8453)",
        "Destination: Ethereum (chainId 1)",
        `HLX to migrate (1:1): ${amountLine}`,
        `Nonce: ${nonce}`,
        `Timestamp: ${timestamp}`,
        "",
        "This signature is gasless and does not move funds. Distribution",
        "occurs after verification, within 24 hours.",
      ].join("\n");

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const sig = await signer.signMessage(message);
      setSignature(sig);
      setReferenceId(
        ethers.utils.keccak256(sig).slice(2, 10).toUpperCase()
      );
      setStage("processing");
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const copyReference = async () => {
    try {
      await navigator.clipboard.writeText(referenceId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  /* ──────────────────────────── Render ──────────────────────────── */

  return (
    <div className="w-full min-h-screen bg-white">
      <ComponentWrapper styles="pt-12 sm:pt-20 pb-24">
        <Header />

        {stage !== "intro" && (
          <Stepper current={STAGE_TO_STEP[stage] ?? 0} />
        )}

        <div ref={cardRef}>
          {stage === "intro" && <IntroStage onStart={() => setStage("connect")} />}

          {stage === "connect" && (
            <ConnectStage
              busy={busy}
              error={error}
              onDismissError={() => setError(null)}
              onConnect={connectAndLoad}
            />
          )}

          {stage === "review" && (
            <ReviewStage
              account={account}
              balance={balance}
              balanceError={balanceError}
              busy={busy}
              error={error}
              onDismissError={() => setError(null)}
              onSign={signOwnershipClaim}
              onBack={() => {
                setError(null);
                setStage("connect");
              }}
            />
          )}

          {stage === "processing" && <ProcessingStage processStep={processStep} />}

          {stage === "success" && (
            <SuccessStage
              account={account}
              balance={balance}
              balanceError={balanceError}
              referenceId={referenceId}
              signature={signature}
              copied={copied}
              onCopy={copyReference}
            />
          )}
        </div>
      </ComponentWrapper>
    </div>
  );
};

export default MigratePage;

/* ──────────────────────────── Sub-components ──────────────────────────── */

function Header() {
  return (
    <div className="max-w-[760px] mb-10">
      <Eyebrow>HLX Token Migration</Eyebrow>
      <h1 className="text-[34px] sm:text-[46px] font-semibold text-blue-1 leading-tight mb-4">
        Migrate your HLX from Base to Ethereum
      </h1>
      <p className="text-[16px] sm:text-[18px] text-gray-1 leading-[28px]">
        Claim ownership of the wallet you KYC-verified during the Helix token
        sale, reverse any AI agent wallet funding, and migrate your HLX 1:1 to
        the Ethereum blockchain.
      </p>
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <p className="text-[13px] text-blue-2 font-semibold mb-2 tracking-wide uppercase">
      {children}
    </p>
  );
}

function Stepper({ current }) {
  return (
    <div className="mb-8 max-w-[680px]">
      <div className="flex items-center">
        {STEPPER.map((label, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <React.Fragment key={label}>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div
                  className={`flex items-center justify-center w-[28px] h-[28px] rounded-full text-[13px] font-semibold transition-all duration-300 ${
                    done
                      ? "bg-blue-2 text-white"
                      : active
                      ? "bg-blue-2/10 text-blue-2 border-2 border-blue-2"
                      : "bg-gray-2 text-gray-1"
                  }`}
                >
                  {done ? <FiCheck className="w-4 h-4" /> : i + 1}
                </div>
                <span
                  className={`text-[13px] font-medium hidden sm:inline ${
                    active
                      ? "text-blue-1"
                      : done
                      ? "text-blue-2"
                      : "text-gray-1"
                  }`}
                >
                  {label}
                </span>
              </div>
              {i < STEPPER.length - 1 && (
                <div
                  className={`flex-1 h-[2px] mx-2 sm:mx-3 rounded-full transition-colors duration-300 ${
                    i < current ? "bg-blue-2" : "bg-gray-2"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`w-full max-w-[680px] border border-gray-2 rounded-[20px] p-6 sm:p-8 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

function ErrorNote({ children, onDismiss }) {
  if (!children) return null;
  return (
    <div className="mt-4 flex items-start gap-2 rounded-[12px] bg-red-50 border border-red-100 px-4 py-3">
      <FiAlertCircle className="w-[18px] h-[18px] text-red-500 mt-[1px] flex-shrink-0" />
      <p className="text-[14px] text-red-600 leading-[20px] flex-1">{children}</p>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-[13px] text-red-400 hover:text-red-600 font-medium"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-2 text-[15px] font-semibold text-white
                 h-[50px] rounded-[14px] bg-blue-1 px-7 w-full sm:w-auto
                 transition-all duration-200 ease-in-out shadow-md hover:shadow-lg
                 transform hover:scale-[1.02] active:scale-95
                 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-2 text-[15px] font-semibold text-blue-1
                 h-[50px] rounded-[14px] border border-gray-2 px-7 w-full sm:w-auto
                 transition-all duration-200 ease-in-out hover:border-blue-2/30 hover:bg-blue-2/5
                 transform hover:scale-[1.02] active:scale-95
                 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
    >
      {children}
    </button>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-gray-2 last:border-b-0">
      <span className="text-[14px] text-gray-1">{label}</span>
      <span
        className={`text-[14px] text-blue-1 font-medium text-right ${
          mono ? "font-mono break-all" : ""
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/* ── Intro ── */

function IntroStage({ onStart }) {
  const steps = [
    {
      title: "Connect & prove ownership",
      body: "Connect the wallet you used in the token sale and sign a gasless ownership claim.",
    },
    {
      title: "We reverse AI agent funding",
      body: "Any AI agent wallet funding tied to your wallet is reversed and your 1:1 amount is computed.",
    },
    {
      title: "Receive HLX on Ethereum",
      body: "Your verified HLX is migrated 1:1 and distributed to your wallet on Ethereum within 24 hours.",
    },
  ];

  return (
    <div className="w-full">
      {/* Network flow */}
      <div className="flex items-center gap-3 sm:gap-5 mb-10 flex-wrap">
        <NetworkPill src="/assets/base.svg" label="Base" />
        <FiArrowRight className="w-6 h-6 text-blue-2 flex-shrink-0" />
        <NetworkPill src="/assets/hlx.png" label="Ethereum" />
        <span className="text-[14px] text-gray-1 font-medium ml-1">
          1:1 migration
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {steps.map((s, i) => (
          <div
            key={i}
            className="border border-gray-2 rounded-[16px] p-5 flex flex-col gap-2"
          >
            <div className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-blue-2/10 text-blue-2 text-[14px] font-semibold mb-1">
              {i + 1}
            </div>
            <h3 className="text-[16px] font-semibold text-blue-1">{s.title}</h3>
            <p className="text-[14px] text-gray-1 leading-[22px]">{s.body}</p>
          </div>
        ))}
      </div>

      <div className="rounded-[12px] bg-blue-2/5 border border-blue-2/10 px-4 py-3 mb-8 max-w-[680px]">
        <p className="text-[13px] text-gray-1 leading-[20px]">
          The ownership claim is a <strong className="text-blue-1">gasless
          signature</strong> — it is non-custodial and never moves your funds.
          Migration is processed off-chain after verification.
        </p>
      </div>

      <PrimaryButton onClick={onStart}>
        Start migration
        <FiArrowRight className="w-4 h-4" />
      </PrimaryButton>
    </div>
  );
}

function NetworkPill({ src, label }) {
  return (
    <div className="inline-flex items-center gap-2 border border-gray-2 rounded-full pl-2 pr-4 py-2">
      <img
        src={src}
        alt={label}
        className="w-[24px] h-[24px] rounded-full object-contain"
      />
      <span className="text-[15px] font-semibold text-blue-1">{label}</span>
    </div>
  );
}

/* ── Connect ── */

function ConnectStage({ busy, error, onDismissError, onConnect }) {
  const noWallet =
    typeof window !== "undefined" && !window.ethereum;

  return (
    <Card>
      <h2 className="text-[22px] font-semibold text-blue-1 mb-2">
        Connect your wallet
      </h2>
      <p className="text-[15px] text-gray-1 leading-[24px] mb-6">
        Connect the wallet you KYC-verified during the token sale. We'll read
        your HLX balance on Base and ask you to sign a gasless ownership claim.
        No funds move during this step.
      </p>

      {noWallet ? (
        <div>
          <p className="text-[14px] text-gray-1 mb-4">
            MetaMask was not detected in this browser.
          </p>
          <a
            href="https://metamask.io/download/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <PrimaryButton>
              Install MetaMask
              <FiExternalLink className="w-4 h-4" />
            </PrimaryButton>
          </a>
        </div>
      ) : (
        <PrimaryButton onClick={onConnect} disabled={busy}>
          {busy ? (
            <>
              <FiLoader className="w-4 h-4 animate-spin" />
              Connecting…
            </>
          ) : (
            <>
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg"
                alt=""
                className="w-[18px] h-[18px]"
              />
              Connect MetaMask
            </>
          )}
        </PrimaryButton>
      )}

      <ErrorNote onDismiss={onDismissError}>{error}</ErrorNote>
    </Card>
  );
}

/* ── Review ── */

function ReviewStage({
  account,
  balance,
  balanceError,
  busy,
  error,
  onDismissError,
  onSign,
  onBack,
}) {
  const amountDisplay =
    balance !== null && !balanceError
      ? `${formatAmount(balance)} HLX`
      : "Verified from on-chain records";

  return (
    <Card>
      <h2 className="text-[22px] font-semibold text-blue-1 mb-2">
        Review &amp; sign your ownership claim
      </h2>
      <p className="text-[15px] text-gray-1 leading-[24px] mb-6">
        Confirm the details below, then sign the gasless ownership claim. This
        proves you own this KYC-verified wallet and authorizes the migration.
      </p>

      <div className="rounded-[14px] border border-gray-2 px-4 sm:px-5 mb-5">
        <InfoRow label="Wallet" value={truncate(account)} mono />
        <InfoRow label="Source network" value="Base" />
        <InfoRow label="Destination network" value="Ethereum" />
        <InfoRow label="HLX to migrate" value={amountDisplay} />
        <InfoRow label="Migration rate" value="1 : 1" />
      </div>

      {balanceError && (
        <p className="text-[13px] text-gray-1 leading-[20px] mb-5">
          We couldn't read your on-chain balance automatically. Don't worry —
          your exact HLX amount will be confirmed from on-chain records during
          verification.
        </p>
      )}

      <div className="rounded-[12px] bg-blue-2/5 border border-blue-2/10 px-4 py-4 mb-6">
        <p className="text-[13px] font-semibold text-blue-1 mb-2">
          By signing, you authorize Helix to:
        </p>
        <ul className="space-y-1.5">
          {[
            "Confirm you own this KYC-verified token sale wallet.",
            "Reverse any AI agent wallet funding tied to this wallet.",
            "Migrate your HLX 1:1 from Base to Ethereum.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-blue-2 mt-[2px] flex-shrink-0" />
              <span className="text-[13px] text-gray-1 leading-[20px]">
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <PrimaryButton onClick={onSign} disabled={busy}>
          {busy ? (
            <>
              <FiLoader className="w-4 h-4 animate-spin" />
              Waiting for signature…
            </>
          ) : (
            <>
              Sign ownership claim
              <FiArrowRight className="w-4 h-4" />
            </>
          )}
        </PrimaryButton>
        <SecondaryButton onClick={onBack} disabled={busy}>
          Back
        </SecondaryButton>
      </div>

      <ErrorNote onDismiss={onDismissError}>{error}</ErrorNote>
    </Card>
  );
}

/* ── Processing ── */

function ProcessingStage({ processStep }) {
  return (
    <Card>
      <h2 className="text-[22px] font-semibold text-blue-1 mb-2">
        Processing your migration…
      </h2>
      <p className="text-[15px] text-gray-1 leading-[24px] mb-6">
        We're preparing your verified claim for distribution. This only takes a
        moment.
      </p>

      <ul className="space-y-3">
        {PROCESS_STEPS.map((label, i) => {
          const done = i < processStep;
          const active = i === processStep;
          return (
            <li
              key={label}
              className={`flex items-center gap-3 rounded-[12px] px-4 py-3 border transition-colors duration-300 ${
                active
                  ? "border-blue-2/30 bg-blue-2/5"
                  : done
                  ? "border-gray-2 bg-white"
                  : "border-gray-2 bg-white opacity-60"
              }`}
            >
              <span className="flex-shrink-0">
                {done ? (
                  <FiCheckCircle className="w-[22px] h-[22px] text-blue-2" />
                ) : active ? (
                  <FiLoader className="w-[22px] h-[22px] text-blue-2 animate-spin" />
                ) : (
                  <span className="block w-[22px] h-[22px] rounded-full border-2 border-gray-2" />
                )}
              </span>
              <span
                className={`text-[15px] ${
                  done || active
                    ? "text-blue-1 font-medium"
                    : "text-gray-1"
                }`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* ── Success ── */

function SuccessStage({
  account,
  balance,
  balanceError,
  referenceId,
  signature,
  copied,
  onCopy,
}) {
  const amountDisplay =
    balance !== null && !balanceError
      ? `${formatAmount(balance)} HLX`
      : "Verified from on-chain records";

  return (
    <Card>
      <div className="flex flex-col items-center text-center mb-6">
        <div className="flex items-center justify-center w-[64px] h-[64px] rounded-full bg-blue-2/10 mb-4">
          <FiCheckCircle className="w-[36px] h-[36px] text-blue-2" />
        </div>
        <h2 className="text-[24px] font-semibold text-blue-1 mb-2">
          Migration request submitted
        </h2>
        <p className="text-[15px] text-gray-1 leading-[24px] max-w-[460px]">
          Your wallet ownership has been signed successfully. Any AI agent
          wallet funding will be reversed, and your HLX will be migrated 1:1 to
          Ethereum and distributed to your wallet{" "}
          <strong className="text-blue-1">within 24 hours</strong> after
          verification.
        </p>
      </div>

      <div className="rounded-[14px] border border-gray-2 px-4 sm:px-5 mb-5">
        <InfoRow label="Wallet" value={truncate(account)} mono />
        <InfoRow label="Amount (1:1)" value={amountDisplay} />
        <InfoRow
          label="Reference ID"
          value={
            <span className="inline-flex items-center gap-2">
              <span className="font-mono">{referenceId}</span>
              <button
                onClick={onCopy}
                className="text-blue-2 hover:text-blue-1 transition-colors"
                aria-label="Copy reference ID"
              >
                {copied ? (
                  <FiCheck className="w-4 h-4" />
                ) : (
                  <FiCopy className="w-4 h-4" />
                )}
              </button>
            </span>
          }
        />
        {signature && (
          <InfoRow label="Signature" value={truncate(signature)} mono />
        )}
      </div>

      <p className="text-[13px] text-gray-1 leading-[20px] mb-6">
        Please keep your Reference ID safe — you may need it for support
        enquiries about your migration.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <AddHLXButton ensureEthereumNetwork={true} />
        <Link to="/" className="w-full sm:w-auto">
          <SecondaryButton>Back to Home</SecondaryButton>
        </Link>
      </div>
    </Card>
  );
}
